import { ReportBuilderMeasure } from "@models/reportBuilder";

export interface MetricAlert {
    title: string;
    description: string;
    time: Date;
    type: 'MISSING_METRIC_SOURCE' | 'MISSING_METRIC_FORMULA' | 'MISSING_ROLLUP_TYPE' | 'MISSING_SOURCE_COLUMN' | 'MISSING_DIMENSION_CONNECTIONS';
    objectId: string;
    objectType: 'measure';
}

export default class MetricValidationService {
    private static instance: MetricValidationService;

    public static getInstance(): MetricValidationService {
        if (!MetricValidationService.instance) {
            MetricValidationService.instance = new MetricValidationService();
        }

        return MetricValidationService.instance;
    }

    constructor() {}

    public validateMetric(metric: ReportBuilderMeasure): MetricAlert[] {
        const alerts: MetricAlert[] = [];
        
        // Check for missing metric source
        if (!metric.pipeline_node_id && !metric.is_calculation) {
            alerts.push({
                title: 'Missing metric source',
                description: 'This metric needs to be connected to a data source',
                time: metric.last_updated || new Date(),
                type: 'MISSING_METRIC_SOURCE',
                objectId: metric.id as string,
                objectType: 'measure'
            });
        }

        // Check for missing formula on calculated metrics
        if (metric.is_calculation && !metric.formula) {
            alerts.push({
                title: 'Missing formula',
                description: 'This calculated metric needs a formula',
                time: metric.last_updated || new Date(),
                type: 'MISSING_METRIC_FORMULA',
                objectId: metric.id as string,
                objectType: 'measure'
            });
        }

        // Check for missing rollup type on non-calculated metrics
        if (!metric.is_calculation && !metric.aggregator) {
            alerts.push({
                title: 'Missing rollup type',
                description: 'This metric needs a rollup type (sum, average, etc.)',
                time: metric.last_updated || new Date(),
                type: 'MISSING_ROLLUP_TYPE',
                objectId: metric.id as string,
                objectType: 'measure'
            });
        }

        // Check for missing source column on non-calculated metrics
        if (!metric.is_calculation && !metric.field_id) {
            alerts.push({
                title: 'Missing source column',
                description: 'This metric needs a source column selected',
                time: metric.last_updated || new Date(),
                type: 'MISSING_SOURCE_COLUMN',
                objectId: metric.id as string,
                objectType: 'measure'
            });
        }

        // Check if any of the dimension join trees have join_tree == null and ignore == false
        if (Array.isArray(metric.dimension_join_trees) && !metric.is_calculation) {
            metric.dimension_join_trees.forEach((dimJoinTree: any) => {
                if (dimJoinTree && dimJoinTree.join_tree == null && dimJoinTree.ignore === false) {
                    alerts.push({
                        title: 'Missing dimension connection',
                        description: 'This metric is missing a required dimension connection.',
                        time: metric.last_updated || new Date(),
                        type: 'MISSING_DIMENSION_CONNECTIONS',
                        objectId: metric.id as string,
                        objectType: 'measure'
                    });
                }
            });
        }

        return alerts;
    }

    public getMetricErrors(metric: ReportBuilderMeasure): string[] {
        return this.validateMetric(metric).map(alert => alert.title);
    }
}

// Convenience exports for common usage
export const validateMetric = (metric: ReportBuilderMeasure): MetricAlert[] => {
    return MetricValidationService.getInstance().validateMetric(metric);
}

export const getMetricErrors = (metric: ReportBuilderMeasure): string[] => {
    return MetricValidationService.getInstance().getMetricErrors(metric);
}